import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useState } from 'react';
import { ArrowUpRight, Check, Copy, Crown, RefreshCw, Users, WalletCards } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCurrentTeam, teamApiError } from './teamApi';
import TeamNotice, { useTeamNotice } from './TeamNotice';

export default function TeamBenefitsPanel({ preview = false, english = false }) {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [copiedTeamId, setCopiedTeamId] = useState('');
  const { notice, showNotice, dismiss } = useTeamNotice();

  const load = useCallback(async () => {
    if (preview) return;
    setLoading(true);
    setError('');
    try {
      setTeam(await getCurrentTeam());
    } catch (loadError) {
      if (loadError?.code === 'TEAM_CONTEXT_MISSING') setTeam(null);
      else setError(getErrorMessage(teamApiError(loadError, '团队信息加载失败')));
    } finally { setLoading(false); }
  }, [english, preview]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!copiedTeamId) return undefined;
    const resetTimer = window.setTimeout(() => setCopiedTeamId(''), 1800);
    return () => window.clearTimeout(resetTimer);
  }, [copiedTeamId]);

  const copyTeamId = async teamId => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(teamId);
      setCopiedTeamId(teamId);
      showNotice(english ? 'Team ID copied. You can share it now.' : '团队 ID 已复制，可以直接发给成员');
    } catch {
      showNotice('复制失败，请手动选择团队 ID', 'error');
    }
  };
  const route = path => preview ? `/prototype/vnext${path}` : path;

  return (
    <div className="account-content__stack team-panel">
      <section className="account-page-heading team-panel__heading">
        <div><h1>{english ? 'Team Benefits' : '团队权益'}</h1><p>{english ? 'Balance, plan and membership at a glance.' : '集中查看团队余额、版本和成员概况'}</p></div>
      </section>
      {loading ? <div className="team-panel__state">{english ? 'Loading teams…' : '正在加载团队…'}</div>
        : error ? <div className="team-panel__state" role="alert"><p>{error}</p><button type="button" onClick={load}><RefreshCw size={14} />{english ? 'Retry' : '重新加载'}</button></div>
          : !team ? <div className="team-panel__state">{preview ? (english ? 'Team data is hidden in preview.' : '预览模式不加载团队数据') : (english ? 'No current team.' : '暂无当前团队')}</div>
            : <div className="team-card-list">
              <section className="team-benefit-card">
                <div className="team-benefit-card__top"><span className="team-benefit-card__avatar">{String(team.name || '小').charAt(0)}</span><div><h2>{team.name}</h2><button className={copiedTeamId === team.team_id ? 'is-copied' : ''} type="button" onClick={() => copyTeamId(team.team_id)}><span>团队 ID：{team.team_id}</span>{copiedTeamId === team.team_id ? <><Check size={12} /><b>{english ? 'Copied' : '已复制'}</b></> : <Copy size={12} />}</button></div></div>
                <div className="team-benefit-card__stats">
                  <Link className="team-benefit-card__stat" to={`${route('/billing/store')}?type=team`}><span>Tapies {english ? 'balance' : '余额'}</span><strong><WalletCards size={14} />{Number(team.credits || 0).toLocaleString()}</strong><small>{english ? 'Top up current team' : '为当前团队直充'}<ArrowUpRight size={10} /></small></Link>
                  <Link className="team-benefit-card__stat" to={`${route('/billing/plan')}?type=team`}><span>{english ? 'Current plan' : '当前版本'}</span><strong><Crown size={14} />{team.plan_name || '免费版'}</strong><small>{team.available_team_plan_name ? `${english ? 'Upgrade to' : '升级'}：${team.available_team_plan_name}` : (english ? 'View plans' : '查看升级套餐')}<ArrowUpRight size={10} /></small></Link>
                  <Link className="team-benefit-card__stat" to={route('/team/settings')}><span>{english ? 'Members' : '团队成员'}</span><strong><Users size={14} />{team.member_count || 0} / {team.member_limit ?? 100}</strong><small>{english ? 'Manage members and quotas' : '管理成员与额度'}<ArrowUpRight size={10} /></small></Link>
                </div>
              </section>
            </div>}
      <TeamNotice notice={notice} onClose={dismiss} />
    </div>
  );
}
